import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ChevronDown, CircleCheck, CircleHelp, Clock, FilePenLine, FileText, Layers, Pencil, Plus, Target,
  Trash2, TriangleAlert, Trophy, X,
} from 'lucide-react';
import {
  listCourseExams,
  getExam,
  listExamAttempts,
  gradeExamAttempt,
  createExam,
  updateExam,
  publishExam,
  archiveExam,
  deleteExam,
} from '../../api/exams';
import { getCourseModules } from '../../api/modules';
import { getLessons } from '../../api/lessons';
import { unwrapList } from '../../utils/api';
import errorText from '../../utils/errorText';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import MetricCard from '../../components/MetricCard/MetricCard';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import Modal from '../../components/Modal/Modal';
import { useConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import Dropdown from '../../components/Dropdown/Dropdown';
import { useI18n } from '../../i18n/I18nContext';
import { pluralKey } from '../../utils/plural';
import AnswerOptions from '../CourseQuizzes/AnswerOptions';
import MediaSlot from '../../components/MediaSlot/MediaSlot';
import './CourseExams.css';

/* ──────────────────────────────────────────────────────────────
 * Legacy description tags are still parsed as a fallback for deployments
 * that have not run the exam metadata migration yet. New writes use real
 * backend fields: scope, moduleId, category, difficulty, sections.
 * ────────────────────────────────────────────────────────────── */

const FINAL_TAG = '[final]';
const MODULE_TAG_RE = /^\[module:([0-9a-f-]{36})\]\s*/i;
const EXAM_CATEGORIES = {
  GENERAL: [
    { value: '', labelKey: 'courseAssess.exams.category.general' },
    { value: 'checkpoint', labelKey: 'courseAssess.exams.category.checkpoint' },
    { value: 'review', labelKey: 'courseAssess.exams.category.review' },
  ],
  LANGUAGE: [
    { value: 'grammar', labelKey: 'courseAssess.exams.category.grammar' },
    { value: 'vocabulary', labelKey: 'courseAssess.exams.category.vocabulary' },
    { value: 'reading', labelKey: 'courseAssess.exams.category.reading' },
    { value: 'listening', labelKey: 'courseAssess.exams.category.listening' },
    { value: 'writing', labelKey: 'courseAssess.exams.category.writing' },
    { value: 'speaking', labelKey: 'courseAssess.exams.category.speaking' },
  ],
  MATH: [
    { value: 'concepts', labelKey: 'courseAssess.exams.category.concepts' },
    { value: 'procedural-fluency', labelKey: 'courseAssess.exams.category.proceduralFluency' },
    { value: 'word-problems', labelKey: 'courseAssess.exams.category.wordProblems' },
    { value: 'reasoning', labelKey: 'courseAssess.exams.category.reasoning' },
    { value: 'algebra', labelKey: 'courseAssess.exams.category.algebra' },
    { value: 'geometry', labelKey: 'courseAssess.exams.category.geometry' },
    { value: 'data', labelKey: 'courseAssess.exams.category.data' },
  ],
};

const QUESTION_TYPES = [
  ['MULTIPLE_CHOICE', 'courseAssess.exams.questionType.MULTIPLE_CHOICE'],
  ['MULTI_SELECT', 'courseAssess.exams.questionType.MULTI_SELECT'],
  ['TRUE_FALSE', 'courseAssess.exams.questionType.TRUE_FALSE'],
  ['FILL_BLANK', 'courseAssess.exams.questionType.FILL_BLANK'],
  ['SHORT_ANSWER', 'courseAssess.exams.questionType.SHORT_ANSWER'],
  ['NUMERIC', 'courseAssess.exams.questionType.NUMERIC'],
  ['MATCHING', 'courseAssess.exams.questionType.MATCHING'],
  ['ORDERING', 'courseAssess.exams.questionType.ORDERING'],
  ['ESSAY', 'courseAssess.exams.questionType.ESSAY'],
  ['SPEAKING', 'courseAssess.exams.questionType.SPEAKING'],
];

function tagFromExam(exam) {
  if (exam.scope === 'FINAL') return { kind: 'final' };
  if (exam.scope === 'MODULE') return { kind: 'module', moduleId: exam.moduleId };
  if (exam.scope === 'PRACTICE') return { kind: 'other' };
  const desc = exam.description || '';
  if (desc.startsWith(FINAL_TAG)) return { kind: 'final' };
  const m = desc.match(MODULE_TAG_RE);
  if (m) return { kind: 'module', moduleId: m[1] };
  return { kind: 'other' };
}

/** A module's lessons in course order. */
// The lessons students see: the API puts an exam behind a draft at the module's end.
const lessonsOfModule = (lessons, moduleId) => lessons
  .filter((l) => l.moduleId === moduleId && l.isPublished !== false)
  .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));

/**
 * The 1-based position of the lesson a checkpoint follows, or null when it sits at the module's end:
 * no lesson chosen, the lesson left the module, or it is the last one.
 */
function placementIndex(exam, lessons) {
  if (!exam.afterLessonId) return null;
  const inModule = lessonsOfModule(lessons, exam.moduleId);
  const at = inModule.findIndex((l) => l.id === exam.afterLessonId);
  return at < 0 || at === inModule.length - 1 ? null : at + 1;
}

function plainDescription(exam) {
  return (exam.description || '')
    .replace(/^\[final\]\s*/, '')
    .replace(MODULE_TAG_RE, '')
    .trimStart();
}

const ATTEMPT_STATUSES = ['SUBMITTED', 'GRADED', 'IN_PROGRESS', 'EXPIRED'];

// An answer the student left blank ("") is no answer, like one that was never sent
const hasAnswer = (value) => value != null && String(value).trim() !== '';

function attemptStatusLabel(status, t) {
  if (!status) return '';
  return ATTEMPT_STATUSES.includes(status) ? t(`courseAssess.exams.attemptStatus.${status}`) : status;
}

/* ──────────────────────────────────────────────────────────────
 * Status badge + exam card
 * ────────────────────────────────────────────────────────────── */

function ExamStatus({ status }) {
  const { t } = useI18n();
  const s = (status || '').toUpperCase();
  if (s === 'PUBLISHED') {
    return <span className="ds-badge ds-badge-success ds-badge--dot">{t('courseAssess.exams.statusPublished')}</span>;
  }
  if (s === 'ARCHIVED') {
    return <span className="ds-badge ds-badge-neutral">{t('courseAssess.exams.statusArchived')}</span>;
  }
  return <span className="ds-badge ds-badge-warning">{t('courseAssess.exams.statusDraft')}</span>;
}

function ExamCard({ exam, isFinal, moduleTitle, afterLesson, onEdit, onReview, onDelete, onTogglePublish }) {
  const { t, lang } = useI18n();
  const questionsCount = Array.isArray(exam.questions) ? exam.questions.length : 0;
  const pendingReviewCount = exam._count?.pendingReview ?? exam.pendingReviewCount ?? 0;
  const description = plainDescription(exam);
  const published = exam.status === 'PUBLISHED';
  // "General" and "Mixed" are what an exam gets when nothing was chosen: they say nothing on a card
  const category = (exam.category || '').toUpperCase();
  const difficulty = (exam.difficulty || '').toUpperCase();
  const categoryLabel = category && category !== 'GENERAL' && ({
    LANGUAGE: t('courseAssess.exams.categoryLanguage'),
    MATH: t('courseAssess.exams.categoryMath'),
  }[category] || exam.category.toLowerCase());
  const difficultyLabel = difficulty && difficulty !== 'MIXED' && ({
    EASY: t('courseAssess.exams.difficultyEasy'),
    MEDIUM: t('courseAssess.exams.difficultyMedium'),
    HARD: t('courseAssess.exams.difficultyHard'),
  }[difficulty] || exam.difficulty.toLowerCase());

  return (
    <article className={`section-card exam-card ${isFinal ? 'is-final' : ''}`}>
      <div className="exam-card-top">
        <span className="ui-icon-chip" data-tone={isFinal ? 'brand' : 'purple'} aria-hidden="true">{isFinal ? <Trophy /> : <Layers />}</span>
        <div className="exam-card-badges">
          <ExamStatus status={exam.status} />
          {categoryLabel && <span className="ds-badge ds-badge-neutral">{categoryLabel}</span>}
          {difficultyLabel && <span className="ds-badge ds-badge-neutral">{difficultyLabel}</span>}
        </div>
      </div>

      <div className="exam-card-head">
        <h3 className="exam-card-title" title={exam.title}>{exam.title}</h3>
        {moduleTitle && !isFinal && <p className="exam-card-module" title={moduleTitle}>{moduleTitle}</p>}
        {afterLesson && <p className="exam-card-module">{t('courseAssess.exams.placementAfterShort', { index: afterLesson })}</p>}
      </div>
      {description && <p className="exam-card-desc">{description}</p>}

      <ul className="exam-card-stats">
        <li>
          <CircleHelp size={15} aria-hidden="true" />
          {t(pluralKey('courseAssess.exams.questionsStat', questionsCount, lang), { count: questionsCount })}
        </li>
        <li>
          <Target size={15} aria-hidden="true" />
          {t('courseAssess.exams.passStat', { percent: exam.passingScore ?? 60 })}
        </li>
        <li>
          <Clock size={15} aria-hidden="true" />
          {exam.timeLimit ? t('courseAssess.exams.timeStat', { count: exam.timeLimit }) : t('courseAssess.exams.noTimeStat')}
        </li>
      </ul>

      <div className="exam-card-actions">
        <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => onEdit(exam)}>
          <Pencil aria-hidden="true" />
          {t('courseAssess.exams.edit')}
        </button>
        <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => onReview(exam)}>
          {pendingReviewCount ? t('courseAssess.exams.reviewWithCount', { count: pendingReviewCount }) : t('courseAssess.exams.review')}
        </button>
        <div className="exam-card-foot">
          <button
            type="button"
            className={`ds-btn ds-btn-sm ${published ? 'ds-btn-ghost' : 'ds-btn-primary-soft'}`}
            onClick={() => onTogglePublish(exam)}
          >
            {published ? t('courseAssess.exams.unpublish') : t('courseAssess.exams.publish')}
          </button>
          <button
            type="button"
            className="ui-icon-btn ui-icon-btn--danger"
            onClick={() => onDelete(exam)}
            aria-label={t('courseAssess.exams.delete')}
            title={t('courseAssess.exams.delete')}
          >
            <Trash2 aria-hidden="true" />
          </button>
        </div>
      </div>
    </article>
  );
}

/* ──────────────────────────────────────────────────────────────
 * Exam form (modal-style slide panel)
 * ────────────────────────────────────────────────────────────── */

const BLANK_FORM = {
  title: '',
  description: '',
  instructions: '',
  scope: 'final',         // 'final' | 'module' | 'other'
  moduleId: '',
  afterLessonId: '',      // the lesson a module exam follows; empty = the module's end
  category: 'GENERAL',
  subCategory: '',
  difficulty: 'MIXED',
  examKind: 'checkpoint',
  sections: [],
  questions: [],
  passingScore: 60,
  timeLimit: 30,
  maxAttempts: 3,
  showResults: true,
  showAnswers: false,
  shuffleQuestions: true,
  shuffleOptions: true,
};

function makeLocalId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function scopeToApi(scope) {
  if (scope === 'final') return 'FINAL';
  if (scope === 'module') return 'MODULE';
  return 'PRACTICE';
}

function scopeDefaults(scope) {
  if (scope === 'final') {
    return { passingScore: 85, maxAttempts: 1, showAnswers: false, shuffleQuestions: true, shuffleOptions: true, difficulty: 'MIXED', examKind: 'mock' };
  }
  if (scope === 'module') {
    return { passingScore: 70, maxAttempts: 3, showAnswers: false, shuffleQuestions: true, shuffleOptions: true, difficulty: 'MEDIUM', examKind: 'checkpoint' };
  }
  return { passingScore: 0, maxAttempts: '', showAnswers: true, shuffleQuestions: false, shuffleOptions: false, difficulty: 'MIXED', examKind: 'drill' };
}

// Single choice and multiple choice are edited as option cards (AnswerOptions); true / false is two
// fixed cards. The key of these types is the TEXT of the marked option(s): that is what the student
// player sends and what the grader compares, case-insensitively.
const CHOICE_TYPES = ['MULTIPLE_CHOICE', 'MULTI_SELECT'];
const EXAM_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
// The player stores "true" / "false"; the key is kept as the seeded exams write it
const TRUE_FALSE_KEYS = ['True', 'False'];

const splitLines = (text) => String(text || '').split('\n').map((v) => v.trim()).filter(Boolean);
const sameText = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

/** The marked options of a stored choice key: the options whose text the key names. */
function correctIndices(type, options, key) {
  if (type === 'TRUE_FALSE') {
    const idx = TRUE_FALSE_KEYS.findIndex((k) => key != null && sameText(k, key));
    return idx >= 0 ? [idx] : [];
  }
  if (!CHOICE_TYPES.includes(type) || key == null) return [];
  const keys = Array.isArray(key) ? key : [key];
  const picked = options.reduce((out, opt, i) => (keys.some((k) => opt.trim() && sameText(opt, k)) ? [...out, i] : out), []);
  return type === 'MULTIPLE_CHOICE' ? picked.slice(0, 1) : picked;
}

/** What the teacher can change about the answer key: when it is unchanged the key goes back as loaded. */
function answerSignature(q) {
  if (CHOICE_TYPES.includes(q.type)) return JSON.stringify([q.options, q.correct]);
  if (q.type === 'TRUE_FALSE') return JSON.stringify(q.correct);
  return q.correctAnswerText;
}

function normalizeQuestionForForm(question) {
  const type = question.type || 'MULTIPLE_CHOICE';
  // The text box cannot show a MATCHING answer map, so it stays empty for objects.
  const correctAnswerText = Array.isArray(question.correctAnswer)
    ? question.correctAnswer.join('\n')
    : question.correctAnswer == null || typeof question.correctAnswer === 'object'
      ? ''
      : String(question.correctAnswer);
  const options = Array.isArray(question.options) ? question.options.map((o) => String(o ?? '')) : [];
  // A choice question always shows at least two cards to type into
  while (CHOICE_TYPES.includes(type) && options.length < 2) options.push('');
  const form = {
    id: question.id || makeLocalId('q'),
    type,
    question: question.question || '',
    options,
    correct: correctIndices(type, options, question.correctAnswer),
    correctAnswerText,
    points: question.points ?? 1,
    explanation: question.explanation || '',
    sectionId: question.sectionId || '',
    difficulty: question.difficulty || 'MEDIUM',
    skillSlug: question.skillSlug || '',
    tolerance: question.tolerance ?? '',
    acceptedAnswersText: Array.isArray(question.acceptedAnswers) ? question.acceptedAnswers.join('\n') : '',
    rubricText: Array.isArray(question.rubric)
      ? question.rubric.map((r) => `${r.label}:${r.points}${r.description ? `:${r.description}` : ''}`).join('\n')
      : '',
    pairsText: Array.isArray(question.pairs)
      ? question.pairs.map((p) => `${p.left}=${p.right}`).join('\n')
      : '',
    // An ORDERING question saved by the old editor kept its words in `options`
    itemsText: Array.isArray(question.items)
      ? question.items.join('\n')
      : type === 'ORDERING' && Array.isArray(question.options) ? question.options.join('\n') : '',
    autoGraded: question.autoGraded !== false,
    // The picture is edited on the question card (MediaSlot); media rides along so a save writes it back.
    imageUrl: question.imageUrl,
    imageAlt: question.imageAlt,
    media: question.media,
  };
  // The answer key as loaded, for keys the form cannot hold (see serializeQuestion).
  form.loadedAnswer = { type, value: question.correctAnswer, signature: answerSignature(form) };
  return form;
}

/** A new question: single choice, four empty options, none marked right. */
function blankQuestion() {
  return normalizeQuestionForForm({ id: makeLocalId('q'), points: 1, options: ['', '', '', ''] });
}

function serializeQuestion(question) {
  const lines = splitLines(question.correctAnswerText);
  const acceptedAnswers = splitLines(question.acceptedAnswersText);
  const base = {
    id: question.id,
    type: question.type,
    question: question.question.trim(),
    points: Number(question.points) || 1,
    explanation: question.explanation.trim() || undefined,
    sectionId: question.sectionId || undefined,
    difficulty: question.difficulty || undefined,
    skillSlug: question.skillSlug.trim() || undefined,
    autoGraded: question.type === 'ESSAY' || question.type === 'SPEAKING' ? false : question.autoGraded,
  };
  // Write back the picture and media that were loaded. Only keys the API knows: it rejects others.
  if (question.imageUrl) base.imageUrl = question.imageUrl;
  if (question.imageAlt) base.imageAlt = question.imageAlt;
  if (question.media && typeof question.media === 'object') base.media = question.media;

  if (CHOICE_TYPES.includes(question.type)) {
    const options = question.options.map((o) => o.trim());
    const picked = question.correct.map((i) => options[i]).filter(Boolean);
    base.options = options.filter(Boolean);
    base.correctAnswer = question.type === 'MULTI_SELECT' ? picked : picked[0];
  } else if (question.type === 'TRUE_FALSE') {
    base.options = [...TRUE_FALSE_KEYS];
    base.correctAnswer = TRUE_FALSE_KEYS[question.correct[0]];
  } else if (question.type === 'NUMERIC') {
    const text = question.correctAnswerText.trim();
    base.correctAnswer = text === '' ? undefined : Number(text);
    if (question.tolerance !== '') base.tolerance = Number(question.tolerance) || 0;
  } else if (question.type === 'MATCHING') {
    base.items = splitLines(question.itemsText);
    base.pairs = splitLines(question.pairsText).map((row) => {
      const [left, right] = row.split('=');
      return { left: (left || '').trim(), right: (right || '').trim() };
    }).filter((p) => p.left && p.right);
    // The player answers MATCHING with a left → right map and the grader compares the key as stored,
    // so the key always follows the pairs
    base.correctAnswer = base.pairs.length
      ? Object.fromEntries(base.pairs.map((p) => [p.left, p.right]))
      : (lines.length ? lines : undefined);
  } else if (question.type === 'ORDERING') {
    // The player shows `items` as a word bank and sends the sentence the student built
    base.items = splitLines(question.itemsText);
    base.correctAnswer = lines.length ? lines.join(' ') : undefined;
  } else {
    base.correctAnswer = question.correctAnswerText.trim() || undefined;
  }

  // An answer key the form cannot hold as such (an ORDERING sentence, a NUMERIC object, "true" in
  // lower case) goes back as loaded unless the answer or the question type was edited. MATCHING
  // always follows its pairs.
  const loaded = question.loadedAnswer;
  if (
    loaded && loaded.value != null && question.type !== 'MATCHING' &&
    loaded.type === question.type && loaded.signature === answerSignature(question)
  ) {
    base.correctAnswer = loaded.value;
  }

  if (acceptedAnswers.length) base.acceptedAnswers = acceptedAnswers;
  if (question.rubricText.trim()) {
    base.rubric = question.rubricText.split('\n').map((row) => {
      const [label, points, description] = row.split(':');
      return { label: (label || '').trim(), points: Number(points) || 0, description: (description || '').trim() || undefined };
    }).filter((r) => r.label);
  }
  return base;
}

// Words of an ORDERING sentence as the grader reads them: case, ё and trailing punctuation do not count
const orderTokens = (text) => String(text || '')
  .toLowerCase()
  .replace(/ё/g, 'е')
  .split(/\s+/)
  .map((w) => w.replace(/^[«"'(]+|[.,!?…;:»"')]+$/g, ''))
  .filter(Boolean)
  .sort();

/**
 * What stops a question from being graded right, as `quizAuthoring.exams.issue.*` codes. A question
 * whose key names no option (or no option at all) would mark every student wrong, so the save waits
 * until the teacher fixes it. Questions without text are handled apart (the form asks about them).
 */
function questionIssues(question) {
  const issues = [];
  const { type } = question;
  if (CHOICE_TYPES.includes(type)) {
    const filled = question.options.map((o) => o.trim()).filter(Boolean);
    if (filled.length < 2) issues.push('needOptions');
    const lower = filled.map((o) => o.toLowerCase());
    if (new Set(lower).size !== lower.length) issues.push('duplicateOptions');
    if (question.correct.length === 0) issues.push(type === 'MULTI_SELECT' ? 'pickCorrectMany' : 'pickCorrect');
    else if (question.correct.some((i) => !question.options[i]?.trim())) issues.push('correctEmpty');
    return issues;
  }
  if (type === 'TRUE_FALSE') {
    if (question.correct.length !== 1) issues.push('pickCorrect');
    return issues;
  }
  const key = serializeQuestion(question).correctAnswer;
  if (type === 'NUMERIC') {
    const value = key && typeof key === 'object' ? Number(key.value) : key;
    if (typeof value !== 'number' || !Number.isFinite(value)) issues.push('needNumber');
  } else if (type === 'FILL_BLANK' || type === 'SHORT_ANSWER') {
    if (!key && !splitLines(question.acceptedAnswersText).length) issues.push('needAnswer');
  } else if (type === 'ORDERING') {
    const items = splitLines(question.itemsText);
    if (items.length < 2) issues.push('needItems');
    if (!key) issues.push('needOrder');
    else if (items.length >= 2) {
      const answer = Array.isArray(key) ? key.join(' ') : String(key);
      if (JSON.stringify(orderTokens(answer)) !== JSON.stringify(orderTokens(items.join(' ')))) issues.push('orderMismatch');
    }
  } else if (type === 'MATCHING') {
    const pairs = splitLines(question.pairsText).filter((row) => {
      const [left, right] = row.split('=');
      return (left || '').trim() && (right || '').trim();
    });
    if (pairs.length < 2) issues.push('needPairs');
  }
  return issues;
}

/** Form changes when the question type is switched: the options survive, a mark that no longer fits does not. */
function retypeQuestion(question, type) {
  const wasChoice = CHOICE_TYPES.includes(question.type);
  const patch = { type };
  if (CHOICE_TYPES.includes(type)) {
    const options = [...question.options];
    while (options.length < 4 && options.every((o) => !o.trim())) options.push('');
    while (options.length < 2) options.push('');
    patch.options = options;
    // Single choice keeps a mark only when exactly one option was marked
    patch.correct = !wasChoice ? [] : type === 'MULTIPLE_CHOICE' ? (question.correct.length === 1 ? question.correct : []) : question.correct;
  } else {
    patch.correct = [];
  }
  if (type === 'ORDERING' && !question.itemsText && question.options.some((o) => o.trim())) {
    patch.itemsText = question.options.filter((o) => o.trim()).join('\n');
  }
  return { ...question, ...patch };
}

/** The form an exam opens with: every field of an existing exam, or the defaults of a new one. */
function buildForm(mode, initial) {
  // Edit mode: hydrate every field from the existing exam.
  if (mode === 'edit' && initial?.id) {
    const tag = tagFromExam(initial);
    return {
      title: initial.title || '',
      description: plainDescription(initial),
      instructions: initial.instructions || '',
      scope: tag.kind,
      moduleId: tag.kind === 'module' ? tag.moduleId : '',
      afterLessonId: tag.kind === 'module' ? initial.afterLessonId || '' : '',
      category: initial.category || 'GENERAL',
      subCategory: initial.subCategory || '',
      difficulty: initial.difficulty || 'MIXED',
      examKind: initial.examKind || 'checkpoint',
      sections: Array.isArray(initial.sections) ? initial.sections : [],
      questions: Array.isArray(initial.questions) ? initial.questions.map(normalizeQuestionForForm) : [],
      passingScore: initial.passingScore ?? 60,
      timeLimit: initial.timeLimit || '',
      maxAttempts: initial.maxAttempts || '',
      showResults: !!initial.showResults,
      showAnswers: !!initial.showAnswers,
      shuffleQuestions: !!initial.shuffleQuestions,
      shuffleOptions: !!initial.shuffleOptions,
    };
  }
  // Create mode: start from BLANK_FORM (with sensible defaults), but
  // honour any scope/module preset passed in via `initial`.
  const presetTag = initial ? tagFromExam(initial) : null;
  return {
    ...BLANK_FORM,
    scope: presetTag?.kind || 'final',
    moduleId: presetTag?.kind === 'module' ? presetTag.moduleId : '',
    ...scopeDefaults(presetTag?.kind || 'final'),
  };
}

// Whole numbers only: the API rejects a fractional pass mark, limit or attempt count with a 400
const wholeNumber = (value, min, max = Infinity) => {
  const n = Number(String(value ?? '').trim());
  return String(value ?? '').trim() !== '' && Number.isInteger(n) && n >= min && n <= max;
};

/** Problems with the exam's own numbers, as quizAuthoring keys. Empty limits mean "no limit". */
function settingsIssues(form) {
  const issues = [];
  if (!wholeNumber(form.passingScore, 0, 100)) issues.push('quizAuthoring.common.passMarkInvalid');
  if (String(form.timeLimit ?? '').trim() !== '' && !wholeNumber(form.timeLimit, 1)) issues.push('quizAuthoring.exams.timeLimitInvalid');
  if (String(form.maxAttempts ?? '').trim() !== '' && !wholeNumber(form.maxAttempts, 1)) issues.push('quizAuthoring.exams.attemptsInvalid');
  return issues;
}

function ExamForm({ open, mode, initial, modules, lessons, onClose, onSubmit, saving }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState(BLANK_FORM);
  // Problems are listed once a save was refused, and then follow every edit until they are fixed
  const [showIssues, setShowIssues] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const issuesRef = useRef(null);

  const [confirm, confirmDialog] = useConfirmDialog();
  // What the form opened with: closing it with something else asks first
  const openedWith = useRef('');

  useEffect(() => {
    if (!open) return;
    const next = buildForm(mode, initial);
    openedWith.current = JSON.stringify(next);
    setForm(next);
    setShowIssues(false);
    setAdvancedOpen(false);
  }, [open, initial, mode]);

  const issues = useMemo(() => {
    const out = settingsIssues(form).map((key) => ({ key, text: t(key) }));
    form.questions.forEach((q, i) => {
      if (!q.question.trim()) return;
      questionIssues(q).forEach((code) => out.push({
        key: `${q.id}-${code}`,
        questionId: q.id,
        text: t(`quizAuthoring.exams.issue.${code}`, { index: i + 1 }),
      }));
    });
    return out;
  }, [form, t]);
  const invalidQuestions = useMemo(() => new Set(issues.map((i) => i.questionId).filter(Boolean)), [issues]);

  // Cancel, the cross and Esc all come here: an exam that took ten minutes to write is not lost to a stray key
  const requestClose = async () => {
    if (JSON.stringify(form) !== openedWith.current) {
      const leave = await confirm({
        title: t('courseAssess.exams.discardTitle'),
        description: t('courseAssess.exams.discardDesc'),
        confirmLabel: t('courseAssess.exams.discardAction'),
        tone: 'danger',
      });
      if (!leave) return;
    }
    onClose();
  };

  if (!open) return null;

  const placementLessons = lessonsOfModule(lessons, form.moduleId);
  const titleValid = form.title.trim().length > 0;
  const moduleValid = form.scope !== 'module' || !!form.moduleId;
  const canSave = !saving && titleValid && moduleValid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    if (issues.length) {
      setShowIssues(true);
      setTimeout(() => issuesRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
      return;
    }
    // A question without text is not saved: say which ones, instead of dropping them without a word
    const blank = form.questions.map((q, i) => (q.question.trim() ? null : i + 1)).filter(Boolean);
    if (blank.length) {
      const goOn = await confirm({
        title: t(pluralKey('quizAuthoring.exams.emptyTextTitle', blank.length, lang), { count: blank.length }),
        description: t('quizAuthoring.exams.emptyTextDesc', {
          numbers: blank.map((n) => t('courseAssess.exams.questionNumber', { index: n })).join(', '),
        }),
        confirmLabel: t('quizAuthoring.exams.emptyTextSave'),
        cancelLabel: t('quizAuthoring.exams.emptyTextBack'),
        tone: 'neutral',
      });
      if (!goOn) return;
    }
    const questions = form.questions
      .filter((q) => q.question.trim())
      .map(serializeQuestion);
    // Emptying a limit while editing must clear it (null); a new exam just leaves it out.
    const noLimit = mode === 'edit' ? null : undefined;
    // Only a changed place is sent: a save that does not touch it leaves it alone.
    const afterLessonId = form.scope === 'module' ? form.afterLessonId || null : null;
    onSubmit({
      title: form.title.trim(),
      description: form.description.trim() || undefined,
      // Emptied instructions are only sent (as '') when the exam had some to clear.
      instructions: form.instructions.trim() || (mode === 'edit' && initial?.instructions ? '' : undefined),
      scope: scopeToApi(form.scope),
      moduleId: form.scope === 'module' ? form.moduleId : undefined,
      afterLessonId: afterLessonId === (initial?.afterLessonId ?? null) ? undefined : afterLessonId,
      category: form.category,
      subCategory: form.subCategory || undefined,
      difficulty: form.difficulty,
      examKind: form.examKind || undefined,
      sections: form.sections,
      questions,
      passingScore: Number(form.passingScore),
      timeLimit: String(form.timeLimit ?? '').trim() ? Number(form.timeLimit) : noLimit,
      maxAttempts: String(form.maxAttempts ?? '').trim() ? Number(form.maxAttempts) : noLimit,
      showResults: !!form.showResults,
      showAnswers: !!form.showAnswers,
      shuffleQuestions: !!form.shuffleQuestions,
      shuffleOptions: !!form.shuffleOptions,
    });
  };

  const setScope = (scope) => {
    setForm({
      ...form,
      scope,
      moduleId: scope === 'module' ? form.moduleId : '',
      afterLessonId: scope === 'module' ? form.afterLessonId : '',
      ...scopeDefaults(scope),
    });
  };

  const addSection = () => {
    setForm({
      ...form,
      sections: [
        ...form.sections,
        { id: makeLocalId('section'), title: t('courseAssess.exams.newSectionDefaultTitle'), skill: '', instructions: '', order: form.sections.length },
      ],
    });
  };

  const updateSection = (idx, patch) => {
    setForm({
      ...form,
      sections: form.sections.map((section, i) => (i === idx ? { ...section, ...patch } : section)),
    });
  };

  const removeSection = (idx) => {
    const removed = form.sections[idx];
    setForm({
      ...form,
      sections: form.sections.filter((_, i) => i !== idx),
      questions: form.questions.map((q) => q.sectionId === removed?.id ? { ...q, sectionId: '' } : q),
    });
  };

  const addQuestion = () => {
    setForm({ ...form, questions: [...form.questions, blankQuestion()] });
  };

  const updateQuestion = (idx, patch) => {
    setForm({
      ...form,
      questions: form.questions.map((question, i) => (i === idx ? { ...question, ...patch } : question)),
    });
  };

  const changeQuestionType = (idx, type) => {
    setForm({
      ...form,
      questions: form.questions.map((question, i) => (i === idx ? retypeQuestion(question, type) : question)),
    });
  };

  const removeQuestion = (idx) => {
    setForm({ ...form, questions: form.questions.filter((_, i) => i !== idx) });
  };

  // Option cards of a choice question: marks are option positions, so removing an option moves the later marks up
  const toggleCorrect = (idx, optIdx) => {
    const q = form.questions[idx];
    if (q.type === 'MULTI_SELECT') {
      updateQuestion(idx, { correct: q.correct.includes(optIdx) ? q.correct.filter((i) => i !== optIdx) : [...q.correct, optIdx].sort((a, b) => a - b) });
    } else {
      updateQuestion(idx, { correct: [optIdx] });
    }
  };
  const changeOption = (idx, optIdx, value) => {
    updateQuestion(idx, { options: form.questions[idx].options.map((o, i) => (i === optIdx ? value : o)) });
  };
  const addOption = (idx) => {
    updateQuestion(idx, { options: [...form.questions[idx].options, ''] });
  };
  const removeOption = (idx, optIdx) => {
    const q = form.questions[idx];
    updateQuestion(idx, {
      options: q.options.filter((_, i) => i !== optIdx),
      correct: q.correct.filter((i) => i !== optIdx).map((i) => (i > optIdx ? i - 1 : i)),
    });
  };

  const goToQuestion = (questionId) => {
    document.getElementById(`exam-q-${questionId}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  };

  return (
    <Modal
      open
      onClose={requestClose}
      size="xl"
      closeOnBackdrop={false}
      title={mode === 'edit' ? t('courseAssess.exams.editExamTitle') : t('courseAssess.exams.newExamTitle')}
      className="exam-modal"
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn-secondary" onClick={requestClose}>{t('courseAssess.exams.cancel')}</button>
          <button type="submit" form="exam-form" className={`ds-btn ds-btn-primary ${saving ? 'is-loading' : ''}`} disabled={!canSave}>
            {mode === 'edit' ? t('courseAssess.exams.saveChanges') : t('courseAssess.exams.createExam')}
          </button>
        </>
      )}
    >
      <form id="exam-form" className="exam-form" onSubmit={handleSubmit} noValidate>
        <p className="exam-form-subtitle">{t('courseAssess.exams.formSubtitle')}</p>
        <div className="exam-form-body">
          {showIssues && issues.length > 0 && (
            <div ref={issuesRef} className="ui-alert exam-issues" role="alert">
              <TriangleAlert aria-hidden="true" />
              <div>
                <p className="exam-issues-title">{t('quizAuthoring.exams.issuesTitle')}</p>
                <ul>
                  {issues.map((issue) => (
                    <li key={issue.key}>
                      {issue.questionId ? (
                        <button type="button" className="exam-issue-link" onClick={() => goToQuestion(issue.questionId)}>{issue.text}</button>
                      ) : issue.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Scope selector */}
          <div className="exam-form-row">
            <span className="exam-form-label">{t('courseAssess.exams.examScopeLabel')}</span>
            <div className="exam-scope-pills" role="radiogroup" aria-label={t('courseAssess.exams.examScopeLabel')}>
              <button
                type="button"
                role="radio"
                aria-checked={form.scope === 'final'}
                className={`exam-scope-pill ${form.scope === 'final' ? 'is-active' : ''}`}
                onClick={() => setScope('final')}
              >
                <span className="exam-scope-icon"><Trophy size={18} aria-hidden="true" /></span>
                <span className="exam-scope-label">{t('courseAssess.exams.finalExamOption')}</span>
                <span className="exam-scope-sub">{t('courseAssess.exams.finalExamOptionSub')}</span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={form.scope === 'module'}
                className={`exam-scope-pill ${form.scope === 'module' ? 'is-active' : ''}`}
                onClick={() => setScope('module')}
              >
                <span className="exam-scope-icon"><Layers size={18} aria-hidden="true" /></span>
                <span className="exam-scope-label">{t('courseAssess.exams.moduleExamOption')}</span>
                <span className="exam-scope-sub">{t('courseAssess.exams.moduleExamOptionSub')}</span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={form.scope === 'other'}
                className={`exam-scope-pill ${form.scope === 'other' ? 'is-active' : ''}`}
                onClick={() => setScope('other')}
              >
                <span className="exam-scope-icon"><Target size={18} aria-hidden="true" /></span>
                <span className="exam-scope-label">{t('courseAssess.exams.practiceOption')}</span>
                <span className="exam-scope-sub">{t('courseAssess.exams.practiceOptionSub')}</span>
              </button>
            </div>
          </div>

          {form.scope === 'module' && (
            <div className="exam-form-row">
              <label className="exam-form-label" htmlFor="exam-module">{t('courseAssess.exams.moduleLabel')} <em>*</em></label>
              <Dropdown
                id="exam-module"
                value={form.moduleId}
                onChange={(val) => setForm({ ...form, moduleId: val, afterLessonId: '' })}
                options={modules.map((m) => ({ value: m.id, label: m.title }))}
                placeholder={t('courseAssess.exams.selectModulePlaceholder')}
                ariaLabel={t('courseAssess.exams.moduleLabel')}
              />
            </div>
          )}

          {form.scope === 'module' && placementLessons.length > 1 && (
            <div className="exam-form-row">
              <label className="exam-form-label" htmlFor="exam-placement">{t('courseAssess.exams.placementLabel')}</label>
              <Dropdown
                id="exam-placement"
                value={placementLessons.some((l) => l.id === form.afterLessonId) ? form.afterLessonId : ''}
                onChange={(val) => setForm({ ...form, afterLessonId: val })}
                options={[
                  { value: '', label: t('courseAssess.exams.placementEnd') },
                  // After the last lesson is the module's end, unless the exam already sits there.
                  ...placementLessons
                    .map((l, i) => ({
                      value: l.id,
                      label: t('courseAssess.exams.placementAfter', { index: i + 1, title: l.title }),
                    }))
                    .filter((o, i) => i < placementLessons.length - 1 || o.value === form.afterLessonId),
                ]}
                ariaLabel={t('courseAssess.exams.placementLabel')}
              />
              <span className="exam-form-hint">{t('courseAssess.exams.placementHint')}</span>
            </div>
          )}

          <div className="exam-form-row">
            <label className="exam-form-label" htmlFor="exam-title">
              {t('courseAssess.exams.titleLabel')} <em>*</em>
            </label>
            <input
              id="exam-title"
              data-autofocus
              type="text"
              value={form.title}
              placeholder={t('courseAssess.exams.titlePlaceholder')}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
          </div>

          <div className="exam-form-row">
            <label className="exam-form-label" htmlFor="exam-desc">{t('courseAssess.exams.descriptionLabel')}</label>
            <textarea
              id="exam-desc"
              value={form.description}
              placeholder={t('courseAssess.exams.descriptionPlaceholder')}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
            />
          </div>

          <div className="exam-form-row">
            <label className="exam-form-label" htmlFor="exam-instructions">{t('courseAssess.exams.examInstructionsLabel')}</label>
            <textarea
              id="exam-instructions"
              value={form.instructions}
              placeholder={t('courseAssess.exams.examInstructionsPlaceholder')}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              rows={3}
            />
          </div>

          <div className="exam-form-grid exam-form-grid--4">
            <div className="exam-form-row">
              <label className="exam-form-label" htmlFor="exam-pass">{t('courseAssess.exams.passingScoreLabel')}</label>
              <input
                id="exam-pass"
                type="number"
                min={0}
                max={100}
                step={1}
                inputMode="numeric"
                value={form.passingScore}
                aria-invalid={!wholeNumber(form.passingScore, 0, 100) || undefined}
                onChange={(e) => setForm({ ...form, passingScore: e.target.value })}
              />
            </div>
            <div className="exam-form-row">
              <label className="exam-form-label" htmlFor="exam-time">{t('courseAssess.exams.timeLimitLabel')}</label>
              <input
                id="exam-time"
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                placeholder={t('courseAssess.exams.timeLimitPlaceholder')}
                value={form.timeLimit}
                onChange={(e) => setForm({ ...form, timeLimit: e.target.value })}
              />
            </div>
            <div className="exam-form-row">
              <label className="exam-form-label" htmlFor="exam-attempts">{t('courseAssess.exams.maxAttemptsLabel')}</label>
              <input
                id="exam-attempts"
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                placeholder={t('courseAssess.exams.maxAttemptsPlaceholder')}
                value={form.maxAttempts}
                onChange={(e) => setForm({ ...form, maxAttempts: e.target.value })}
              />
            </div>
            <div className="exam-form-row">
              <label className="exam-form-label" htmlFor="exam-difficulty">{t('courseAssess.exams.difficultyLabel')}</label>
              <Dropdown
                id="exam-difficulty"
                value={form.difficulty}
                onChange={(val) => setForm({ ...form, difficulty: val })}
                options={[
                  { value: 'EASY', label: t('courseAssess.exams.difficultyEasy') },
                  { value: 'MEDIUM', label: t('courseAssess.exams.difficultyMedium') },
                  { value: 'HARD', label: t('courseAssess.exams.difficultyHard') },
                  { value: 'MIXED', label: t('courseAssess.exams.difficultyMixed') },
                ]}
                ariaLabel={t('courseAssess.exams.difficultyLabel')}
              />
            </div>
          </div>

          <div className="exam-builder-block">
            <div className="exam-builder-head">
              <div>
                <span className="exam-form-label">{t('courseAssess.exams.questionsLabel')}</span>
                <p>{t('courseAssess.exams.questionsDesc')}</p>
              </div>
              <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={addQuestion}>
                <Plus aria-hidden="true" />
                {t('courseAssess.exams.addQuestion')}
              </button>
            </div>
            {form.questions.length === 0 ? (
              <div className="exam-builder-empty">{t('courseAssess.exams.noQuestionsYet')}</div>
            ) : (
              <div className="exam-builder-list">
                {form.questions.map((question, idx) => (
                  <ExamQuestionEditor
                    key={question.id}
                    question={question}
                    index={idx}
                    sections={form.sections}
                    invalid={showIssues && invalidQuestions.has(question.id)}
                    onChange={(patch) => updateQuestion(idx, patch)}
                    onChangeType={(type) => changeQuestionType(idx, type)}
                    onRemove={() => removeQuestion(idx)}
                    onToggleCorrect={(optIdx) => toggleCorrect(idx, optIdx)}
                    onChangeOption={(optIdx, value) => changeOption(idx, optIdx, value)}
                    onAddOption={() => addOption(idx)}
                    onRemoveOption={(optIdx) => removeOption(idx, optIdx)}
                  />
                ))}
              </div>
            )}
            {form.questions.length > 0 && (
              <button type="button" className="ds-btn ds-btn-secondary exam-add-question" onClick={addQuestion}>
                <Plus aria-hidden="true" />
                {t('courseAssess.exams.addQuestion')}
              </button>
            )}
          </div>

          <div className="exam-form-row">
            <span className="exam-form-label">{t('courseAssess.exams.behaviourLabel')}</span>
            <div className="exam-toggles">
              <ToggleRow
                label={t('courseAssess.exams.showResultsLabel')}
                hint={t('courseAssess.exams.showResultsHint')}
                value={form.showResults}
                onChange={(v) => setForm({ ...form, showResults: v })}
              />
              <ToggleRow
                label={t('courseAssess.exams.showAnswersLabel')}
                hint={t('courseAssess.exams.showAnswersHint')}
                value={form.showAnswers}
                onChange={(v) => setForm({ ...form, showAnswers: v })}
              />
              <ToggleRow
                label={t('courseAssess.exams.shuffleQuestionsLabel')}
                hint={t('courseAssess.exams.shuffleQuestionsHint')}
                value={form.shuffleQuestions}
                onChange={(v) => setForm({ ...form, shuffleQuestions: v })}
              />
              <ToggleRow
                label={t('courseAssess.exams.shuffleOptionsLabel')}
                hint={t('courseAssess.exams.shuffleOptionsHint')}
                value={form.shuffleOptions}
                onChange={(v) => setForm({ ...form, shuffleOptions: v })}
              />
            </div>
          </div>

          {/* Rarely used: grouping and labels the student player barely shows */}
          <div className={`exam-advanced ${advancedOpen ? 'is-open' : ''}`}>
            <button
              type="button"
              className="exam-advanced-toggle"
              aria-expanded={advancedOpen}
              aria-controls="exam-advanced-body"
              onClick={() => setAdvancedOpen((v) => !v)}
            >
              <ChevronDown className="exam-advanced-chevron" size={18} aria-hidden="true" />
              <span className="exam-advanced-text">
                <span className="exam-advanced-title">{t('quizAuthoring.exams.advancedSettings')}</span>
                <span className="exam-advanced-hint">{t('quizAuthoring.exams.advancedSettingsHint')}</span>
              </span>
            </button>
            {advancedOpen && (
              <div id="exam-advanced-body" className="exam-advanced-body">
                <div className="exam-form-grid">
                  <div className="exam-form-row">
                    <label className="exam-form-label" htmlFor="exam-category">{t('courseAssess.exams.categoryLabel')}</label>
                    <Dropdown
                      id="exam-category"
                      value={form.category}
                      onChange={(val) => setForm({ ...form, category: val, subCategory: '' })}
                      options={[
                        { value: 'GENERAL', label: t('courseAssess.exams.categoryGeneral') },
                        { value: 'LANGUAGE', label: t('courseAssess.exams.categoryLanguage') },
                        { value: 'MATH', label: t('courseAssess.exams.categoryMath') },
                      ]}
                      ariaLabel={t('courseAssess.exams.categoryLabel')}
                    />
                  </div>
                  <div className="exam-form-row">
                    <label className="exam-form-label" htmlFor="exam-subcategory">{t('courseAssess.exams.subcategoryLabel')}</label>
                    <Dropdown
                      id="exam-subcategory"
                      value={form.subCategory}
                      onChange={(val) => setForm({ ...form, subCategory: val })}
                      options={(EXAM_CATEGORIES[form.category] || EXAM_CATEGORIES.GENERAL).map((option) => ({
                        value: option.value,
                        label: t(option.labelKey),
                      }))}
                      ariaLabel={t('courseAssess.exams.subcategoryLabel')}
                    />
                  </div>
                  <div className="exam-form-row">
                    <label className="exam-form-label" htmlFor="exam-kind">{t('courseAssess.exams.examKindLabel')}</label>
                    <Dropdown
                      id="exam-kind"
                      value={form.examKind}
                      onChange={(val) => setForm({ ...form, examKind: val })}
                      options={[
                        { value: 'checkpoint', label: t('courseAssess.exams.examKindCheckpoint') },
                        { value: 'mock', label: t('courseAssess.exams.examKindMock') },
                        { value: 'drill', label: t('courseAssess.exams.examKindDrill') },
                        { value: 'challenge', label: t('courseAssess.exams.examKindChallenge') },
                        { value: 'placement', label: t('courseAssess.exams.examKindPlacement') },
                      ]}
                      ariaLabel={t('courseAssess.exams.examKindLabel')}
                    />
                  </div>
                </div>

                <div className="exam-builder-block">
                  <div className="exam-builder-head">
                    <div>
                      <span className="exam-form-label">{t('courseAssess.exams.sectionsLabel')}</span>
                      <p>{t('courseAssess.exams.sectionsDesc')}</p>
                    </div>
                    <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={addSection}>
                      <Plus aria-hidden="true" />
                      {t('courseAssess.exams.addSection')}
                    </button>
                  </div>
                  {form.sections.length === 0 ? (
                    <div className="exam-builder-empty">{t('courseAssess.exams.noSectionsYet')}</div>
                  ) : (
                    <div className="exam-builder-list">
                      {form.sections.map((section, idx) => (
                        <div className="exam-section-editor" key={section.id}>
                          <input
                            type="text"
                            value={section.title}
                            placeholder={t('courseAssess.exams.sectionTitlePlaceholder')}
                            aria-label={`${t('courseAssess.exams.sectionLabel')}: ${t('courseAssess.exams.sectionTitlePlaceholder')}`}
                            onChange={(e) => updateSection(idx, { title: e.target.value })}
                          />
                          <input
                            type="text"
                            value={section.skill || ''}
                            placeholder={t('courseAssess.exams.skillSlugPlaceholder')}
                            aria-label={`${t('courseAssess.exams.sectionLabel')}: ${t('courseAssess.exams.skillSlugPlaceholder')}`}
                            onChange={(e) => updateSection(idx, { skill: e.target.value })}
                          />
                          <input
                            type="text"
                            value={section.instructions || ''}
                            placeholder={t('courseAssess.exams.instructionsPlaceholder')}
                            aria-label={`${t('courseAssess.exams.sectionLabel')}: ${t('courseAssess.exams.instructionsPlaceholder')}`}
                            onChange={(e) => updateSection(idx, { instructions: e.target.value })}
                          />
                          <button
                            type="button"
                            className="ui-icon-btn ui-icon-btn--danger"
                            onClick={() => removeSection(idx)}
                            aria-label={t('courseAssess.exams.removeSection')}
                            title={t('courseAssess.exams.removeSection')}
                          >
                            <Trash2 aria-hidden="true" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </form>
      {confirmDialog}
    </Modal>
  );
}

/** One question of the exam form: its text, type, points and the answer fields its type needs. */
function ExamQuestionEditor({
  question, index, sections, invalid, onChange, onChangeType, onRemove,
  onToggleCorrect, onChangeOption, onAddOption, onRemoveOption,
}) {
  const { t } = useI18n();
  const { id, type } = question;
  const title = t('courseAssess.exams.questionNumber', { index: index + 1 });
  return (
    <div className={`exam-question-editor ${invalid ? 'is-invalid' : ''}`.trim()} id={`exam-q-${id}`}>
      <div className="exam-question-editor-head">
        <strong>{title}</strong>
        <button
          type="button"
          className="ui-icon-btn ui-icon-btn--danger"
          onClick={onRemove}
          aria-label={`${title}: ${t('courseAssess.exams.removeQuestion')}`}
          title={t('courseAssess.exams.removeQuestion')}
        >
          <Trash2 aria-hidden="true" />
        </button>
      </div>
      <textarea
        value={question.question}
        aria-label={title}
        placeholder={t('courseAssess.exams.questionPromptPlaceholder')}
        rows={2}
        onChange={(e) => onChange({ question: e.target.value })}
      />
      <MediaSlot
        kind="image"
        className="exam-question-media"
        value={question.imageUrl || ''}
        onChange={(url) => onChange({ imageUrl: url || null })}
      />
      <div className="exam-form-grid">
        <div className="exam-form-row">
          <label className="exam-form-label" htmlFor={`${id}-type`}>{t('courseAssess.exams.typeLabel')}</label>
          <Dropdown
            id={`${id}-type`}
            value={type}
            onChange={onChangeType}
            options={QUESTION_TYPES.map(([value, labelKey]) => ({ value, label: t(labelKey) }))}
            ariaLabel={t('courseAssess.exams.typeLabel')}
          />
        </div>
        {/* Only an exam split into sections (Qo'shimcha sozlamalar) has a section to pick */}
        {(sections.length > 0 || question.sectionId) && (
          <div className="exam-form-row">
            <label className="exam-form-label" htmlFor={`${id}-section`}>{t('courseAssess.exams.sectionLabel')}</label>
            <Dropdown
              id={`${id}-section`}
              value={question.sectionId}
              onChange={(val) => onChange({ sectionId: val })}
              options={[
                { value: '', label: t('courseAssess.exams.noSectionOption') },
                ...sections.map((section) => ({ value: section.id, label: section.title })),
              ]}
              ariaLabel={t('courseAssess.exams.sectionLabel')}
            />
          </div>
        )}
        <div className="exam-form-row">
          <label className="exam-form-label" htmlFor={`${id}-points`}>{t('courseAssess.exams.pointsLabel')}</label>
          <input id={`${id}-points`} type="number" min={1} value={question.points} onChange={(e) => onChange({ points: e.target.value })} />
        </div>
      </div>

      {CHOICE_TYPES.includes(type) && (
        <AnswerOptions
          options={question.options}
          correct={question.correct}
          letters={EXAM_LETTERS}
          multiple={type === 'MULTI_SELECT'}
          label={t('courseAssess.quizzes.answerOptionsLabel')}
          labelClassName="exam-form-label"
          onToggleCorrect={onToggleCorrect}
          onChangeOption={onChangeOption}
          onAddOption={onAddOption}
          onRemoveOption={onRemoveOption}
        />
      )}
      {type === 'TRUE_FALSE' && (
        <AnswerOptions
          options={[t('quizAuthoring.options.trueOption'), t('quizAuthoring.options.falseOption')]}
          correct={question.correct}
          letters={['A', 'B']}
          label={t('courseAssess.exams.correctAnswerLabel')}
          labelClassName="exam-form-label"
          onToggleCorrect={onToggleCorrect}
        />
      )}
      {type === 'MATCHING' && (
        <div className="exam-form-row">
          <label className="exam-form-label" htmlFor={`${id}-pairs`}>{t('courseAssess.exams.matchingPairsLabel')}</label>
          <textarea
            id={`${id}-pairs`}
            value={question.pairsText}
            placeholder={t('courseAssess.exams.matchingPairsPlaceholder')}
            rows={3}
            onChange={(e) => onChange({ pairsText: e.target.value })}
          />
        </div>
      )}
      {type === 'ORDERING' && (
        <div className="exam-form-grid exam-form-grid--2">
          <div className="exam-form-row">
            <label className="exam-form-label" htmlFor={`${id}-items`}>{t('quizAuthoring.exams.itemsLabel')}</label>
            <textarea
              id={`${id}-items`}
              value={question.itemsText}
              placeholder={t('quizAuthoring.exams.itemsPlaceholder')}
              rows={4}
              onChange={(e) => onChange({ itemsText: e.target.value })}
            />
          </div>
          <div className="exam-form-row">
            <label className="exam-form-label" htmlFor={`${id}-answers`}>{t('quizAuthoring.exams.correctOrderLabel')}</label>
            <textarea
              id={`${id}-answers`}
              value={question.correctAnswerText}
              placeholder={t('quizAuthoring.exams.correctOrderPlaceholder')}
              rows={4}
              onChange={(e) => onChange({ correctAnswerText: e.target.value })}
            />
          </div>
        </div>
      )}
      {['FILL_BLANK', 'SHORT_ANSWER', 'NUMERIC'].includes(type) && (
        <div className="exam-form-row">
          <label className="exam-form-label" htmlFor={`${id}-answer`}>{t('courseAssess.exams.correctAnswerLabel')}</label>
          <input
            id={`${id}-answer`}
            type={type === 'NUMERIC' ? 'number' : 'text'}
            value={question.correctAnswerText}
            placeholder={t('courseAssess.exams.correctAnswerPlaceholder')}
            onChange={(e) => onChange({ correctAnswerText: e.target.value })}
          />
        </div>
      )}
      {['SHORT_ANSWER', 'FILL_BLANK'].includes(type) && (
        <div className="exam-form-row">
          <label className="exam-form-label" htmlFor={`${id}-accepted`}>{t('courseAssess.exams.acceptedAnswersLabel')}</label>
          <textarea
            id={`${id}-accepted`}
            value={question.acceptedAnswersText}
            placeholder={t('courseAssess.exams.acceptedAnswersPlaceholder')}
            rows={2}
            onChange={(e) => onChange({ acceptedAnswersText: e.target.value })}
          />
        </div>
      )}
      {type === 'NUMERIC' && (
        <div className="exam-form-row">
          <label className="exam-form-label" htmlFor={`${id}-tolerance`}>{t('courseAssess.exams.toleranceLabel')}</label>
          <input
            id={`${id}-tolerance`}
            type="number"
            min={0}
            step="0.01"
            value={question.tolerance}
            placeholder={t('courseAssess.exams.tolerancePlaceholder')}
            onChange={(e) => onChange({ tolerance: e.target.value })}
          />
        </div>
      )}
      {['ESSAY', 'SPEAKING'].includes(type) && (
        <div className="exam-form-row">
          <label className="exam-form-label" htmlFor={`${id}-rubric`}>{t('courseAssess.exams.rubricLabel')}</label>
          <textarea
            id={`${id}-rubric`}
            value={question.rubricText}
            placeholder={t('courseAssess.exams.rubricPlaceholder')}
            rows={3}
            onChange={(e) => onChange({ rubricText: e.target.value, autoGraded: false })}
          />
        </div>
      )}
      <div className="exam-form-row">
        <label className="exam-form-label" htmlFor={`${id}-explanation`}>{t('courseAssess.exams.explanationLabel')}</label>
        <input
          id={`${id}-explanation`}
          type="text"
          value={question.explanation}
          placeholder={t('courseAssess.exams.explanationPlaceholder')}
          onChange={(e) => onChange({ explanation: e.target.value })}
        />
      </div>
    </div>
  );
}

function ToggleRow({ label, hint, value, onChange }) {
  return (
    <div className="exam-toggle-row">
      <div className="exam-toggle-text">
        <strong>{label}</strong>
        {hint && <span>{hint}</span>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        className="ui-switch hit-44"
        onClick={() => onChange(!value)}
      />
    </div>
  );
}

function ReviewModal({ exam, attempts, loading, saving, scores, onScoreChange, onGrade, onClose }) {
  const { t } = useI18n();
  const questions = Array.isArray(exam?.questions) ? exam.questions : [];
  const manualQuestions = questions.filter((q) => q.autoGraded === false || q.type === 'ESSAY' || q.type === 'SPEAKING');

  return (
    <Modal
      open={Boolean(exam)}
      onClose={onClose}
      size="xl"
      title={t('courseAssess.exams.reviewAttempts')}
      className="exam-modal"
      footer={<button type="button" className="ds-btn ds-btn-secondary" onClick={onClose}>{t('courseAssess.exams.close')}</button>}
    >
      {exam && (
        <>
          <p className="exam-form-subtitle">{exam.title}</p>
          {loading ? (
            <LoadingState skeleton="list" count={3} message={t('courseAssess.exams.loadingAttempts')} />
          ) : manualQuestions.length === 0 ? (
            <PageEmptyState
              variant="success"
              size="sm"
              title={t('courseAssess.exams.noManualQuestionsTitle')}
              description={t('courseAssess.exams.noManualQuestionsDesc')}
            />
          ) : attempts.length === 0 ? (
            <PageEmptyState
              variant="docs"
              size="sm"
              title={t('courseAssess.exams.noSubmittedAttemptsTitle')}
              description={t('courseAssess.exams.noSubmittedAttemptsDesc')}
            />
          ) : (
            <div className="exam-review-list">
              {attempts.map((attempt) => {
                const answerMap = attempt.answers || {};
                const scoreMap = scores[attempt.id] || {};
                const canGrade = manualQuestions.every((q) => scoreMap[q.id] !== undefined && scoreMap[q.id] !== '');
                return (
                  <div className="exam-review-attempt" key={attempt.id}>
                    <div className="exam-review-attempt-head">
                      <div>
                        <strong>{attempt.user?.name || attempt.user?.email || t('courseAssess.results.unknownStudent')}</strong>
                        <span>
                          {attemptStatusLabel(attempt.status, t)} {attempt.score != null ? `• ${attempt.score}/${attempt.maxScore}` : ''}
                          {attempt.leaveCount > 0
                            ? ` • ${attempt.endedForLeaving
                              ? t('student360.results.endedForLeaving', { count: attempt.leaveCount })
                              : t('student360.results.leftExam', { count: attempt.leaveCount })}`
                            : ''}
                        </span>
                      </div>
                      <button
                        type="button"
                        className={`ds-btn ds-btn-primary ds-btn-sm ${saving ? 'is-loading' : ''}`}
                        disabled={saving || !canGrade}
                        onClick={() => onGrade(attempt, manualQuestions)}
                      >
                        {t('courseAssess.exams.finalizeGrade')}
                      </button>
                    </div>
                    {manualQuestions.map((question) => (
                      <div className="exam-review-question" key={question.id}>
                        <p><strong>{question.question}</strong></p>
                        <blockquote>{hasAnswer(answerMap[question.id]) ? String(answerMap[question.id]) : t('courseAssess.exams.noAnswerText')}</blockquote>
                        {Array.isArray(question.rubric) && question.rubric.length > 0 && (
                          <small>{question.rubric.map((r) => `${r.label} (${r.points})`).join(' • ')}</small>
                        )}
                        <label>
                          {t('courseAssess.exams.scoreOutOf', { points: question.points })}
                          <input
                            type="number"
                            min={0}
                            max={question.points}
                            value={scoreMap[question.id] ?? ''}
                            onChange={(e) => onScoreChange(attempt.id, question.id, e.target.value)}
                          />
                        </label>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

/* ──────────────────────────────────────────────────────────────
 * Main page component
 * ────────────────────────────────────────────────────────────── */

// The modules list of the course workspace, where an exam's module is created first
const modulesPath = (courseId) => `/courses/edit/${courseId}?tab=curriculum&c=modules`;

const questionCount = (exam) => (Array.isArray(exam.questions) ? exam.questions.length : exam._count?.questions ?? null);

function CourseExams({ courseId: courseIdProp } = {}) {
  const { t, lang } = useI18n();
  const [confirm, confirmDialog] = useConfirmDialog();
  const navigate = useNavigate();
  const params = useParams();
  const courseId = courseIdProp || params.courseId || params.id;

  const [exams, setExams] = useState([]);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  // null | 'forbidden' | 'failed': the first load could not show the page at all
  const [loadError, setLoadError] = useState(null);
  // A message about the last action (save, publish, delete, grade, refresh)
  const [error, setError] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [reviewExam, setReviewExam] = useState(null);
  const [reviewAttempts, setReviewAttempts] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewScores, setReviewScores] = useState({});

  // `silent` refreshes the lists in place: after an action the page, and any dialog open over it,
  // must not be swapped for the loading skeleton.
  const load = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const [examsRes, modulesRes, lessonsRes] = await Promise.all([
        listCourseExams(courseId),
        getCourseModules(courseId).catch(() => []),
        getLessons(courseId, { limit: 200 }).catch(() => []),
      ]);
      const items = unwrapList(examsRes);
      setExams(items);
      setModules(unwrapList(modulesRes));
      setLessons(unwrapList(lessonsRes));
      setLoadError(null);
    } catch (e) {
      if (silent) setError(t('courseAssess.exams.loadExamsFailed'));
      else setLoadError(e.code === 403 ? 'forbidden' : 'failed');
    } finally {
      setLoading(false);
    }
  }, [courseId, t]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const out = {
      finals: [],
      byModule: new Map(),    // moduleId → its exams, in the order they sit in the module
      modulesWithoutExam: [],
      other: [],
    };
    exams.forEach((ex) => {
      const tag = tagFromExam(ex);
      if (tag.kind === 'final') {
        // Every final exam is listed: students see each of them, so a second one must not hide here
        out.finals.push(ex);
      } else if (tag.kind === 'module') {
        out.byModule.set(tag.moduleId, [...(out.byModule.get(tag.moduleId) || []), ex]);
      } else {
        out.other.push(ex);
      }
    });
    out.byModule.forEach((list) => list.sort((a, b) => (
      (placementIndex(a, lessons) ?? Infinity) - (placementIndex(b, lessons) ?? Infinity)
    )));
    out.modulesWithoutExam = modules.filter((m) => !out.byModule.has(m.id));
    // Most recently updated first
    out.finals.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
    return out;
  }, [exams, modules, lessons]);

  const stats = useMemo(() => {
    const total = exams.length;
    const published = exams.filter((e) => e.status === 'PUBLISHED').length;
    const drafts = total - published;
    return { total, published, drafts };
  }, [exams]);

  // "Yangi imtihon" starts where the course needs an exam: the final one while there is none, else a
  // module that has none (picked already when it is the only one), else a practice exam
  const defaultPreset = () => {
    if (grouped.finals.length === 0) return { scope: 'FINAL' };
    const missing = grouped.modulesWithoutExam;
    if (missing.length > 0) return { scope: 'MODULE', moduleId: missing.length === 1 ? missing[0].id : '' };
    return { scope: 'PRACTICE' };
  };

  const openCreate = (preset) => {
    setFormMode('create');
    setEditing(preset || defaultPreset());
    setFormOpen(true);
  };

  const openEdit = async (exam) => {
    setFormMode('edit');
    setFormOpen(true);
    try {
      const full = await getExam(exam.id);
      setEditing(full?.data || full);
    } catch {
      setEditing(exam);
    }
  };

  const openReview = async (exam) => {
    setReviewExam(exam);
    setReviewAttempts([]);
    setReviewScores({});
    setReviewLoading(true);
    try {
      const [fullExamRes, attemptsRes] = await Promise.all([
        getExam(exam.id).catch(() => exam),
        listExamAttempts(exam.id, { status: 'SUBMITTED', limit: 100 }).catch(() => []),
      ]);
      const fullExam = fullExamRes?.data || fullExamRes;
      const attempts = unwrapList(attemptsRes);
      setReviewExam(fullExam);
      setReviewAttempts(attempts);
      const nextScores = {};
      attempts.forEach((attempt) => {
        nextScores[attempt.id] = {};
      });
      setReviewScores(nextScores);
    } catch (e) {
      // A toast: the page's banner sits behind the dialog this happens in
      toast.error(errorText(e, t('courseAssess.exams.loadAttemptsFailed')));
    } finally {
      setReviewLoading(false);
    }
  };

  const setManualScore = (attemptId, questionId, value) => {
    setReviewScores((prev) => ({
      ...prev,
      [attemptId]: {
        ...(prev[attemptId] || {}),
        [questionId]: value,
      },
    }));
  };

  const handleGradeAttempt = async (attempt, manualQuestions) => {
    setReviewSaving(true);
    try {
      const questionScores = {};
      manualQuestions.forEach((q) => {
        questionScores[q.id] = Number(reviewScores[attempt.id]?.[q.id] || 0);
      });
      await gradeExamAttempt(attempt.id, { questionScores });
      toast.success(t('courseAssess.exams.gradeSavedToast'));
      await openReview(reviewExam);
      await load(true);
    } catch (e) {
      toast.error(errorText(e, t('courseAssess.exams.gradeAttemptFailed')));
    } finally {
      setReviewSaving(false);
    }
  };

  const handleSubmit = async (payload) => {
    setSaving(true);
    setError(null);
    try {
      if (formMode === 'edit' && editing?.id) {
        await updateExam(editing.id, payload);
      } else {
        await createExam({
          ...payload,
          courseId,
          questions: payload.questions || [],
        });
      }
      toast.success(t('courseAssess.exams.examSavedToast'));
      setFormOpen(false);
      setEditing(null);
      await load(true);
    } catch (e) {
      // A toast, not the page banner: the form stays open over it and would hide the message
      toast.error(errorText(e, t('courseAssess.exams.saveExamFailed')));
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (exam) => {
    const publishing = exam.status !== 'PUBLISHED';
    // The API refuses to publish an exam without questions; say that, not "could not change the status"
    if (publishing && questionCount(exam) === 0) {
      setError(t('quizAuthoring.exams.publishNeedsQuestions'));
      return;
    }
    try {
      if (!publishing) {
        await archiveExam(exam.id);
        toast.success(t('courseAssess.exams.unpublishedToast'));
      } else {
        await publishExam(exam.id);
        toast.success(t('courseAssess.exams.publishedToast'));
      }
      await load(true);
    } catch (e) {
      const noQuestions = publishing && /at least one question/i.test(e?.message || '');
      setError(noQuestions
        ? t('quizAuthoring.exams.publishNeedsQuestions')
        : errorText(e, t('courseAssess.exams.changeStatusFailed')));
    }
  };

  const askDelete = async (exam) => {
    const ok = await confirm({
      title: t('courseAssess.exams.deleteExamQuestion'),
      description: t('courseAssess.exams.deleteExamDesc', { title: exam.title }),
      confirmLabel: t('courseAssess.exams.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await deleteExam(exam.id);
      toast.success(t('courseAssess.exams.examDeletedToast'));
      await load(true);
    } catch (e) {
      setError(errorText(e, t('courseAssess.exams.deleteExamFailed')));
    }
  };

  const closeForm = () => { setFormOpen(false); setEditing(null); };
  const closeReview = () => { setReviewExam(null); setReviewAttempts([]); setReviewScores({}); };

  if (loading) {
    return (
      <div className="course-exams">
        <LoadingState skeleton="page" message={t('courseAssess.exams.loadingExams')} />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="course-exams">
        {loadError === 'forbidden' ? (
          <PageEmptyState variant="forbidden" size="sm" title={t('courseAssess.exams.accessDenied')} />
        ) : (
          <PageEmptyState variant="error" size="sm" onRetry={() => load()} />
        )}
      </div>
    );
  }

  const moduleExamCards = modules.filter((m) => grouped.byModule.has(m.id));

  return (
    <div className="course-exams">
      <div className="exam-top">
        <div className="exam-intro">
          <p className="exam-lead">{t('courseAssess.exams.pageLead')}</p>
          <button type="button" className="ds-btn ds-btn-primary exam-create" onClick={() => openCreate()}>
            <Plus aria-hidden="true" />
            {t('courseAssess.exams.newExam')}
          </button>
        </div>

        <div className="kpi-row">
          <MetricCard countUp={false} title={t('courseAssess.exams.totalExams')} value={stats.total} icon={<FileText />} tone="info" />
          <MetricCard countUp={false} title={t('courseAssess.exams.published')} value={stats.published} icon={<CircleCheck />} tone="success" />
          <MetricCard countUp={false} title={t('courseAssess.exams.drafts')} value={stats.drafts} icon={<FilePenLine />} tone="brand" />
          <MetricCard countUp={false} title={t('courseAssess.exams.modulesWithoutExam')} value={grouped.modulesWithoutExam.length} icon={<Layers />} tone="coral" />
        </div>
      </div>

      {error && (
        <div className="ui-alert exam-banner" role="alert">
          <TriangleAlert aria-hidden="true" />
          <p>{error}</p>
          <button type="button" className="exam-banner-close" onClick={() => setError(null)} aria-label={t('courseAssess.common.close')}>
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Final exam section */}
      <section className="exam-section">
        <div className="section-head">
          <div>
            <h2 className="section-head__title">{t('courseAssess.exams.finalExamSection')}</h2>
            <p className="section-head__desc">{t('courseAssess.exams.finalExamSectionDesc')}</p>
          </div>
        </div>
        {grouped.finals.length > 1 && (
          <div className="ui-alert exam-banner" data-tone="warning" role="status">
            <TriangleAlert aria-hidden="true" />
            <p>{t(pluralKey('quizAuthoring.exams.multipleFinals', grouped.finals.length, lang), { count: grouped.finals.length })}</p>
          </div>
        )}
        {grouped.finals.length > 0 ? (
          <div className="card-grid">
            {grouped.finals.map((exam) => (
              <ExamCard
                key={exam.id}
                exam={exam}
                isFinal
                onEdit={openEdit}
                onReview={openReview}
                onDelete={askDelete}
                onTogglePublish={handleTogglePublish}
              />
            ))}
          </div>
        ) : (
          <InlineEmpty
            className="exam-inline"
            kind="exam"
            title={t('courseAssess.exams.noFinalExamTitle')}
            description={t('courseAssess.exams.noFinalExamDesc')}
            action={(
              <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => openCreate({ description: FINAL_TAG })}>
                <Plus aria-hidden="true" />
                {t('courseAssess.exams.createFinalExamAction')}
              </button>
            )}
          />
        )}
      </section>

      {/* Module exams section */}
      <section className="exam-section">
        <div className="section-head">
          <div>
            <h2 className="section-head__title">{t('courseAssess.exams.moduleExamsSection')}</h2>
            <p className="section-head__desc">{t('courseAssess.exams.moduleExamsSectionDesc')}</p>
          </div>
        </div>

        {modules.length === 0 ? (
          <InlineEmpty
            className="exam-inline"
            icon={<Layers />}
            tone="neutral"
            title={t('courseAssess.exams.noModulesTitle')}
            description={t('courseAssess.exams.noModulesDesc')}
            action={(
              <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => navigate(modulesPath(courseId))}>
                <Plus aria-hidden="true" />
                {t('quizAuthoring.common.addModuleAction')}
              </button>
            )}
          />
        ) : (
          <>
            {moduleExamCards.length > 0 && (
              <div className="card-grid">
                {moduleExamCards.flatMap((mod) => grouped.byModule.get(mod.id).map((exam) => (
                  <ExamCard
                    key={exam.id}
                    exam={exam}
                    moduleTitle={mod.title}
                    afterLesson={placementIndex(exam, lessons)}
                    onEdit={openEdit}
                    onReview={openReview}
                    onDelete={askDelete}
                    onTogglePublish={handleTogglePublish}
                  />
                )))}
              </div>
            )}

            {grouped.modulesWithoutExam.length > 0 && (
              <div className="section-card exam-missing">
                <div className="exam-missing-head">
                  <strong>{t('courseAssess.exams.modulesWithoutExam')}</strong>
                  <span>{t('courseAssess.exams.modulesWithoutExamDesc')}</span>
                </div>
                <div className="ui-list--feed">
                  {grouped.modulesWithoutExam.map((mod) => (
                    <div className="ui-list-row" key={mod.id}>
                      <span className="ui-list-row__body">
                        <span className="ui-list-row__title">{mod.title}</span>
                      </span>
                      <button
                        type="button"
                        className="ds-btn ds-btn-secondary ds-btn-sm"
                        onClick={() => openCreate({ description: `[module:${mod.id}]` })}
                        aria-label={t('courseAssess.exams.addExamForModuleNamed', { module: mod.title })}
                      >
                        <Plus aria-hidden="true" />
                        {t('courseAssess.exams.addExamForModule')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* Other / practice exams */}
      {grouped.other.length > 0 && (
        <section className="exam-section">
          <div className="section-head">
            <div>
              <h2 className="section-head__title">{t('courseAssess.exams.practiceExamsSection')}</h2>
              <p className="section-head__desc">{t('courseAssess.exams.practiceExamsSectionDesc')}</p>
            </div>
          </div>
          <div className="card-grid">
            {grouped.other.map((ex) => (
              <ExamCard
                key={ex.id}
                exam={ex}
                onEdit={openEdit}
                onReview={openReview}
                onDelete={askDelete}
                onTogglePublish={handleTogglePublish}
              />
            ))}
          </div>
        </section>
      )}

      {/* Form */}
      <ExamForm
        open={formOpen}
        mode={formMode}
        initial={editing}
        modules={modules}
        lessons={lessons}
        saving={saving}
        onClose={closeForm}
        onSubmit={handleSubmit}
      />

      <ReviewModal
        exam={reviewExam}
        attempts={reviewAttempts}
        loading={reviewLoading}
        saving={reviewSaving}
        scores={reviewScores}
        onScoreChange={setManualScore}
        onGrade={handleGradeAttempt}
        onClose={closeReview}
      />

      {confirmDialog}
    </div>
  );
}

// Exported for the round-trip and answer-key tests.
export { normalizeQuestionForForm, serializeQuestion, questionIssues, blankQuestion, retypeQuestion };
export default CourseExams;
